import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { HexMark, Icon, KeyValue, monogram, SectionLabel, SimulatedTag, Stat, StatusDot, StatusPill } from '@hive/ui';
import { api, type Alert, type NetworkOverview, type NetworkVenue, type NetworkVenueDetail } from '../api/client';
import { useLive, useNow } from '../api/hooks';
import { NetworkMap } from '../components/NetworkMap';
import { Shell } from '../components/Shell';
import { ago, dateShort, healthLabel, isSim, num } from '../format';

type Filter = 'all' | 'healthy' | 'degraded' | 'offline' | 'licence' | 'busy';

export function NetworkPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const now = useNow(5000);
  const { data: network, live } = useLive<NetworkOverview>(async () => (await api.GET('/v1/network')).data, []);
  const [filter, setFilter] = useState<Filter>('all');
  const [zoomCity, setZoomCity] = useState<string | null>(null);
  const alertsOpen = params.get('alerts') === 'open';

  const real = network?.venues.find((v) => v.source !== 'simulated');
  const selectedId = params.get('venue') ?? real?.id ?? null;
  const selected = network?.venues.find((v) => v.id === selectedId) ?? null;

  // Start zoomed into the real venue's city so the live devices are visible at once.
  useEffect(() => {
    if (real && zoomCity === null && !params.get('zoom')) setZoomCity(real.city);
  }, [real]); // eslint-disable-line react-hooks/exhaustive-deps

  const venues = network?.venues ?? [];
  const counts = useMemo(
    () => ({
      healthy: venues.filter((v) => v.health === 'healthy').length,
      degraded: venues.filter((v) => v.health === 'degraded').length,
      offline: venues.filter((v) => v.hasOfflineScreen || v.health === 'offline').length,
      licence: venues.filter((v) => v.licenceExpiring).length,
      busy: venues.filter((v) => (v.busy.value ?? 0) >= 70).length,
    }),
    [venues],
  );
  const matches = (v: NetworkVenue) =>
    filter === 'all' ||
    (filter === 'healthy' && v.health === 'healthy') ||
    (filter === 'degraded' && v.health === 'degraded') ||
    (filter === 'offline' && (v.hasOfflineScreen || v.health === 'offline')) ||
    (filter === 'licence' && v.licenceExpiring) ||
    (filter === 'busy' && (v.busy.value ?? 0) >= 70);

  const select = (v: NetworkVenue) => {
    const next = new URLSearchParams(params);
    next.set('venue', v.id);
    setParams(next, { replace: true });
  };
  const openAlert = (a: Alert) => {
    if (!a.venueId) return navigate('/alerts');
    const v = venues.find((x) => x.id === a.venueId);
    if (v) {
      setZoomCity(v.city);
      select(v);
    }
  };
  const k = network?.kpis;
  const offlineAlerts = network?.alerts.filter((a) => a.kind === 'screen_offline').length ?? 0;
  const licenceAlerts = network?.alerts.filter((a) => a.kind === 'licence_expiring' || a.kind === 'rights_expiring').length ?? 0;
  const qoeAlerts = network?.alerts.filter((a) => a.kind === 'qoe_degraded').length ?? 0;

  const chips: { id: Filter; label: string; dot?: 'healthy' | 'degraded' | 'offline' }[] = [
    { id: 'all', label: 'All venues' },
    { id: 'healthy', label: `Healthy · ${counts.healthy}`, dot: 'healthy' },
    { id: 'degraded', label: `Degraded · ${counts.degraded}`, dot: 'degraded' },
    { id: 'offline', label: `Offline · ${counts.offline}`, dot: 'offline' },
    { id: 'licence', label: `Licence expiring · ${counts.licence}` },
    { id: 'busy', label: `Busy now · ${counts.busy}` },
  ];

  return (
    <Shell network={network} live={live}>
      <div className="mc-network">
        <section aria-label="Venue map" className="mc-map">
          <NetworkMap
            venues={venues}
            dimmed={(v) => !matches(v)}
            zoomCity={zoomCity}
            selectedId={selectedId}
            onZoom={setZoomCity}
            onSelect={select}
          />
          <div className="mc-map__top">
            <div className="mc-map__kpis">
              <Stat label="Venues" value={k?.venues ?? '…'} />
              <Stat label="Screens online" value={k ? num(k.screensOnline) : '…'} unit={k ? `of ${k.screensTotal}` : undefined} source={k?.screensOnline.source} />
              <Stat label="Live sessions" value={k ? num(k.liveSessions) : '…'} source={k?.liveSessions.source} />
              <Stat label="Network QoE" value={k ? num(k.networkQoe) : '…'} source={k?.networkQoe.source} />
              <button
                type="button"
                className={`mc-alertbar${network?.alerts.some((a) => a.severity === 'critical' && a.source === 'real') ? ' mc-alertbar--hot' : ''}`}
                onClick={() => setParams(alertsOpen ? {} : { alerts: 'open', ...(selectedId ? { venue: selectedId } : {}) }, { replace: true })}
                aria-expanded={alertsOpen}
              >
                <span aria-hidden="true" className="mc-alertbar__icon">
                  <Icon name="alert" size={16} />
                </span>
                <span className="mc-alertbar__text">
                  <span className="mc-alertbar__title">{network ? `${network.alerts.length} open alert${network.alerts.length === 1 ? '' : 's'}` : '…'}</span>
                  <span className="mc-alertbar__sub">
                    {[
                      offlineAlerts && `${offlineAlerts} offline screen${offlineAlerts === 1 ? '' : 's'}`,
                      qoeAlerts && `${qoeAlerts} degraded QoE`,
                      licenceAlerts && `${licenceAlerts} licence or rights expiring`,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'Nothing needs attention'}
                  </span>
                </span>
              </button>
            </div>
            {alertsOpen && network && (
              <ul className="mc-alerts" aria-label="Open alerts">
                {network.alerts.map((a) => (
                  <li key={a.id}>
                    <button type="button" onClick={() => openAlert(a)}>
                      <StatusDot status={a.severity === 'critical' ? 'offline' : 'degraded'} />
                      <span className="mc-alerts__main">
                        <span className="mc-alerts__venue">
                          {a.venueName ?? 'Several venues'}
                          {a.source === 'simulated' && <SimulatedTag />}
                          {a.stageLabel && a.stage && a.stage > 1 && <span className="mc-tiny mc-warn"> · {a.stageLabel}</span>}
                          {a.assignee && <span className="mc-tiny hv-muted"> · {a.assignee.name}</span>}
                        </span>
                        <span className="hv-muted">{a.message}</span>
                      </span>
                      {a.since && <span className="hv-muted mc-small">{ago(a.since, now)} ago</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mc-chips">
              {chips.map((c) => (
                <button key={c.id} type="button" className={`mc-chip${filter === c.id ? ' mc-chip--on' : ''}`} onClick={() => setFilter(c.id)}>
                  {c.dot && <StatusDot status={c.dot} />}
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mc-map__legend">
            <span>
              <StatusDot status="healthy" />
              Healthy
            </span>
            <span>
              <StatusDot status="degraded" />
              Degraded QoE or expiring licence
            </span>
            <span>
              <StatusDot status="offline" />
              Screen offline
            </span>
            <span>· Number in a hexagon = venues in cluster · click to zoom</span>
            <span className="mc-map__legend-real">· Ringed = real venue with live devices; dashed = simulated estate</span>
          </div>
          <div className="mc-map__zoom">
            <button type="button" aria-label="Zoom in" onClick={() => setZoomCity(selected?.city ?? real?.city ?? 'Dubai')}>
              +
            </button>
            <button type="button" aria-label="Zoom out" onClick={() => setZoomCity(null)}>
              −
            </button>
          </div>
        </section>
        {selected && <VenuePanel venue={selected} now={now} onOpen={() => navigate(`/venue/${selected.id}`)} />}
      </div>
    </Shell>
  );
}

function VenuePanel({ venue, now, onOpen }: { venue: NetworkVenue; now: number; onOpen(): void }) {
  const { data } = useLive<NetworkVenueDetail>(
    async () => (await api.GET('/v1/network/venues/{venueId}', { params: { path: { venueId: venue.id } } })).data,
    [venue.id],
    15_000,
  );
  const sim = venue.source === 'simulated';
  const occ = data?.occupancy;
  const max = Math.max(1, ...(occ?.byHour.map((h) => h.value) ?? [1]));
  return (
    <aside aria-label="Selected venue" className="mc-panel">
      <div className="mc-panel__head">
        <div className="mc-panel__venue">
          <HexMark label={monogram(venue.name)} size={40} tone="deep" />
          <div>
            <span className="mc-panel__name">
              {venue.name}
              {sim && <SimulatedTag title="Simulated venue: not a real device estate" />}
            </span>
            <span className="hv-muted mc-small">
              {[venue.area, venue.city, venue.country].filter(Boolean).join(', ')} · Venue {venue.code}
            </span>
          </div>
        </div>
        <StatusPill status={venue.hasOfflineScreen ? 'offline' : venue.health}>{venue.hasOfflineScreen && venue.health !== 'offline' ? 'Screen offline' : healthLabel[venue.health]}</StatusPill>
      </div>

      {occ && (
        <section aria-label="How busy" className="mc-panel__section">
          <div className="mc-row-between">
            <SectionLabel dense as="span">
              How busy
            </SectionLabel>
            <span className="hv-muted mc-small">
              {occ.sourceLabel} <SimulatedTag title="Occupancy is simulated" />
            </span>
          </div>
          <span className="mc-small">
            <span className="mc-strong">{occ.label}</span> · {occ.percent}% of peak{occ.typicalWait ? ` · typical wait ${occ.typicalWait}` : ''}
          </span>
          <svg viewBox="0 0 340 56" className="mc-bars" role="img" aria-label={`Busyness by hour, now ${occ.percent}%`}>
            {occ.byHour.map((h, i) => {
              const hgt = Math.max(2, (h.value / max) * 44);
              const current = h.hour === occ.currentHour;
              return <rect key={h.hour} x={i * 23} y={52 - hgt} width="18" height={hgt} rx="2" className={current ? 'mc-bars__now' : 'mc-bars__bar'} />;
            })}
          </svg>
          <div className="mc-row-between hv-muted mc-tiny">
            <span>12:00</span>
            <span>16:00</span>
            <span>20:00</span>
            <span>02:00</span>
          </div>
        </section>
      )}

      {data?.licence && (
        <section aria-label="Licence" className="mc-panel__section">
          <SectionLabel dense as="span">
            Licence
          </SectionLabel>
          <div className="mc-grid2">
            <KeyValue label="Licence ID" source={data.licence.source === 'simulated' ? 'simulated' : undefined}>
              {data.licence.licenceNo}
            </KeyValue>
            <KeyValue label="Tier">{data.licence.tier}</KeyValue>
            <KeyValue label="Valid">
              {dateShort(data.licence.validFrom)} – {dateShort(data.licence.validTo)}
            </KeyValue>
            <KeyValue label="Billing">{data.licence.billing ?? '—'}</KeyValue>
          </div>
        </section>
      )}

      {data && (
        <section aria-label="Rights" className="mc-panel__section">
          <SectionLabel dense as="span">
            Rights held
          </SectionLabel>
          <div className="mc-list">
            {data.rights.map((r) => (
              <div key={r.id} className={`mc-list__row${r.status !== 'active' ? ' mc-list__row--warn' : ''}`}>
                <StatusDot status={r.status === 'active' ? 'healthy' : 'degraded'} />
                <span className="mc-strong mc-grow">
                  {r.competition}
                  {r.source === 'simulated' && <SimulatedTag />}
                </span>
                <span className={r.status === 'active' ? 'hv-muted mc-small' : 'mc-small mc-warn'}>
                  {r.status === 'active' ? [r.territory, r.scope, `to ${dateShort(r.validTo)}`].filter(Boolean).join(' · ') : `Expires in ${r.daysLeft} days · renew`}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {data && (
        <section aria-label="Screens rated" className="mc-panel__section">
          <SectionLabel dense as="span">
            Screens rated · {venue.screensOnline} of {venue.screensTotal} online
          </SectionLabel>
          <div className="mc-list">
            {data.screens.map((s) => (
              <Link key={s.id} to={`/screen/${s.id}`} className="mc-list__row mc-list__link">
                <HexMark size={22} tone={s.status === 'offline' ? 'red' : s.status === 'unpaired' ? 'muted' : 'teal'} />
                <span className="mc-strong mc-screen-name">{s.name}</span>
                <span className="hv-muted mc-small mc-grow">
                  {[s.sizeInches && `${s.sizeInches} in`, s.rating?.split(' · ')[0]?.toLowerCase(), s.qoe.value != null && `QoE ${s.qoe.value}`].filter(Boolean).join(' · ')}
                  {isSim(s.qoe) && s.qoe.value != null && <SimulatedTag />}
                </span>
                <span className={`mc-small ${s.status === 'offline' ? 'mc-bad' : 'mc-good'}`}>
                  {s.status === 'offline' ? 'Offline' : s.nowPlaying ? `${s.nowPlaying.status === 'live' ? 'Live' : 'On'} · ${s.nowPlaying.competitionShort ?? s.nowPlaying.title}` : s.status === 'unpaired' ? 'Not paired' : 'Ambient'}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {data && (
        <div className="mc-counters">
          <div>
            <span className="mc-counter">
              {data.nectar.scans}
              {data.nectar.source === 'simulated' && <SimulatedTag />}
            </span>
            <span className="hv-muted mc-tiny">QR scans tonight</span>
          </div>
          <div>
            <span className="mc-counter">
              {data.nectar.orders}
              {data.nectar.source === 'simulated' && <SimulatedTag />}
            </span>
            <span className="hv-muted mc-tiny">Nectar orders</span>
          </div>
          <div>
            <span className="mc-counter">{venue.lastHeartbeatAt ? ago(venue.lastHeartbeatAt, now) : sim ? '—' : 'never'}</span>
            <span className="hv-muted mc-tiny">Last heartbeat</span>
          </div>
        </div>
      )}

      <div className="mc-panel__actions">
        <button type="button" className="hv-btn hv-btn--primary mc-grow" onClick={onOpen}>
          Open venue
        </button>
        <button type="button" className="hv-btn hv-btn--secondary" disabled title="Messaging Keeper is not in the prototype">
          Message Keeper
        </button>
      </div>
      {!data && <p className="hv-muted mc-small">Loading venue…</p>}
    </aside>
  );
}
